import React, { Suspense, lazy, useEffect } from "react";
import { BrowserRouter, Routes as RouterRoutes, Route, useLocation } from "react-router-dom";
import axios from "axios";
import ScrollToTop from "./components/ScrollToTop";
import ErrorBoundary from "./components/ErrorBoundary";
import NotFound from "./pages/NotFound";

const API_BASE = import.meta.env.VITE_API_BASE_URL || 'http://localhost:5000/api';

const PageViewTracker = () => {
  const location = useLocation();
  const sessionId = React.useMemo(() => `sess_${Math.random().toString(36).substring(2, 9)}_${Date.now()}`, []);

  useEffect(() => {
    const trackView = async (isHeartbeat = false) => {
      try {
        await axios.post(`${API_BASE}/dashboard/track`, {
          page: location.pathname + location.search,
          sessionId,
          isHeartbeat
        });
      } catch (error) {
        // Silent fail
      }
    };

    // Initial hit on route change
    trackView();

    // Heartbeat for "Active Now" tracking (every 15 seconds)
    const interval = setInterval(() => {
      if (document.visibilityState === 'visible') {
        trackView(true);
      }
    }, 15000);

    return () => clearInterval(interval);
  }, [location, sessionId]);

  return null;
};

// Lazy load pages for code splitting
const Homepage = lazy(() => import('./pages/homepage'));
const TourDetail = lazy(() => import('./pages/tour-detail'));
const ContactPage = lazy(() => import('./pages/contact'));
const VehicleRental = lazy(() => import('./pages/vehicle-rental'));
const VehicleDetail = lazy(() => import('./pages/vehicle-detail'));
const TourPackages = lazy(() => import('./pages/tour-packages'));
const AboutUs = lazy(() => import('./pages/about-us'));
const AdminLogin = lazy(() => import('./pages/admin/Login'));
const AdminDashboard = lazy(() => import('./pages/admin/Dashboard'));
const PolicyPage = lazy(() => import('./pages/PolicyPage'));

// Loading component
const PageLoader = () => (
  <div className="flex items-center justify-center min-h-screen bg-gray-50">
    <div className="text-center">
      <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      <p className="mt-4 text-gray-600">Loading...</p>
    </div>
  </div>
);

const Routes = () => {
  return (
    <BrowserRouter>
      <PageViewTracker />
      <ErrorBoundary>
        <ScrollToTop />
        <Suspense fallback={<PageLoader />}>
          <RouterRoutes>
            {/* Define your route here */}
            <Route path="/" element={<Homepage />} />
            <Route path="/tour-detail" element={<TourDetail />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/vehicle-rental" element={<VehicleRental />} />
            <Route path="/vehicle-detail" element={<VehicleDetail />} />
            <Route path="/tour-packages" element={<TourPackages />} />
            <Route path="/about-us" element={<AboutUs />} />
            <Route path="/homepage" element={<Homepage />} />
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/admin/dashboard" element={<AdminDashboard />} />
            <Route path="/policy/:type" element={<PolicyPage />} />
            <Route path="*" element={<NotFound />} />
          </RouterRoutes>
        </Suspense>
      </ErrorBoundary>
    </BrowserRouter>
  );
};

export default Routes;
